import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaUser, FaPhone, FaCalendar, FaUsers } from "react-icons/fa";
import "./TravelerDetailsModal.css";

function TravelerDetailsModal({ isOpen, onClose, onSubmit, itemName }) {
  const [travelDate, setTravelDate] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [travellerCount, setTravellerCount] = useState(1);
  const [travellers, setTravellers] = useState([{ name: "", age: "" }]);
  const [submitting, setSubmitting] = useState(false);

  // Reset the submitting flag whenever the modal is reopened
  React.useEffect(() => {
    if (isOpen) setSubmitting(false);
  }, [isOpen]);

  const handleTravellerCountChange = (count) => {
    setTravellerCount(count);
    setTravellers(
      Array.from({ length: count }, (_, i) => ({
        name: travellers[i]?.name || "",
        age: travellers[i]?.age || "",
      }))
    );
  };

  const handleTravellerChange = (index, field, value) => {
    const updated = [...travellers];
    updated[index][field] = value;
    setTravellers(updated);
  };

  const isFormValid =
    travelDate &&
    phoneNumber &&
    email &&
    travellers.every((t) => t.name.trim() !== "" && t.age.trim() !== "");

  const handleSubmitDetails = () => {
    if (!isFormValid || submitting) return;
    setSubmitting(true);

    const details = {
      travelDate,
      phoneNumber,
      email,
      travellerCount,
      travellers,
    };

    onSubmit(details);
    
    // Reset form
    setTravelDate("");
    setPhoneNumber("");
    setEmail("");
    setTravellerCount(1);
    setTravellers([{ name: "", age: "" }]);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="traveler-modal-overlay">
        <motion.div
          className="traveler-modal-card"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          <button className="traveler-modal-close" onClick={onClose}>
            <FaTimes />
          </button>

          <div className="traveler-modal-header">
            <h2>Traveler Details</h2>
            <p>Please provide your details to proceed with booking</p>
            {itemName && <div className="booking-item-name">📦 {itemName}</div>}
          </div>

          <div className="traveler-modal-body">
            {/* Travel Date */}
            <div className="form-group-modal">
              <label>
                <FaCalendar /> Travel Date *
              </label>
              <input
                type="date"
                className="modal-input"
                value={travelDate}
                onChange={(e) => setTravelDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            {/* Contact Details */}
            <div className="form-row-modal">
              <div className="form-group-modal">
                <label>
                  <FaPhone /> Phone Number *
                </label>
                <input
                  type="tel"
                  className="modal-input"
                  placeholder="+91 XXXXX XXXXX"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </div>

              <div className="form-group-modal">
                <label>
                  <FaUser /> Email Address *
                </label>
                <input
                  type="email"
                  className="modal-input"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Number of Travelers */}
            <div className="form-group-modal">
              <label>
                <FaUsers /> Number of Travelers *
              </label>
              <select
                className="modal-select"
                value={travellerCount}
                onChange={(e) =>
                  handleTravellerCountChange(Number(e.target.value))
                }
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? "Person" : "People"}
                  </option>
                ))}
              </select>
            </div>

            {/* Traveler Details */}
            <div className="travelers-list">
              <h3 style={{ fontSize: 16, marginBottom: 16, color: "#1e293b" }}>
                Traveler Information
              </h3>
              {travellers.map((t, i) => (
                <div key={i} className="traveler-entry">
                  <div className="traveler-number">Traveler {i + 1}</div>
                  <div className="traveler-inputs">
                    <input
                      className="modal-input"
                      placeholder="Full Name"
                      value={t.name}
                      onChange={(e) =>
                        handleTravellerChange(i, "name", e.target.value)
                      }
                    />
                    <input
                      className="modal-input"
                      placeholder="Age"
                      type="number"
                      value={t.age}
                      onChange={(e) =>
                        handleTravellerChange(i, "age", e.target.value)
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="traveler-modal-footer">
            <button
              className="btn-modal-submit"
              onClick={handleSubmitDetails}
              disabled={!isFormValid || submitting}
              data-testid="traveler-modal-submit"
            >
              {submitting ? "Adding to cart..." : "Continue to Cart"}
            </button>
            <button
              className="btn-modal-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default TravelerDetailsModal;
